/** 메인 프레젠테이션 리모컨 — Realtime Broadcast/Presence 타입 */

export type RemoteRole = 'presentation' | 'controller';
/** local = Supabase 미연결, 같은 브라우저(BroadcastChannel)만 사용 */
/** limited = 서버 혼잡(동시 연결/속도 제한)으로 원격 일시 중지, 긴 간격으로 재시도 */
export type RemoteConnectionStatus = 'idle' | 'local' | 'limited' | 'connecting' | 'connected' | 'reconnecting' | 'error' | 'disconnected' | 'closed';

/** Presence key = sessionId */
/** 'comm' = 리모컨 안의 가상 대상(소통 프레젠테이션 — 학생 기기) */
export type RemotePresentationKind = 'main' | 'lesson' | 'comm';

export interface RemotePresenceState {
  role: RemoteRole;
  sessionId: string;
  deviceId: string;
  /** 표시 이름 (프레젠: '메인 프레젠테이션 N' 또는 사용자 지정) */
  name: string;
  /** 자동 번호 N (프레젠만) */
  autoNumber?: number;
  /** ms epoch — 탭 시작 시각 */
  since: number;
  /** ms epoch — 마지막 track 시각 (Supabase는 참가/재연결/이름 변경 시에만, 로컬은 5초마다) */
  lastSeen?: number;
  /** 프레젠 종류 — 누락 시 'main'(메인 프레젠테이션). 'lesson'은 수업 프레젠(로컬 전용) */
  presentationKind?: RemotePresentationKind;
  /** 수업 프레젠: 학습내용 제목 */
  title?: string;
  /** 수신 측에서만 설정: 같은 브라우저(BroadcastChannel)로 보이는 대상 */
  isLocal?: boolean;
}

/** 질문 유형 — 누락 시 'short'(단답). 'essay'(서술형)는 정답 채점 없음 */
export type RemoteQuestionType = 'short' | 'ox' | 'choice' | 'essay';

/** 질문 모드 — 누락 시 'submit'(제출만 받기) */
export type RemoteQuestionMode = 'submit' | 'answer';
/** 정답모드 정답 — 유형별 하나만 사용 */
export interface RemoteAnswerKey {
  /** 선택형: 0부터 시작하는 보기 번호 (단일 정답·이전 버전 호환 — 복수 정답이면 첫 번째) */
  choiceIndex?: number;
  /** 선택형 복수 정답(오름차순). 있으면 choiceIndex보다 우선 — 학생이 이 집합과 정확히 같게 골라야 정답 */
  choiceIndexes?: number[];
  ox?: 'O' | 'X';
  /** 단답형: 허용 정답(정규화 후 비교) */
  texts?: string[];
}

export type RemoteNoticeDurationSec = 5 | 10 | 30 | null;

export interface RemotePayloadMap {
  SHOW_NOTICE: { text: string; durationSec: RemoteNoticeDurationSec; speak?: boolean };
  CLOSE_NOTICE: Record<string, never>;
  /** 자막(누르고 말하기): 같은 id 는 제자리 갱신, final 후 잠시 뒤 사라짐. ACK 없음 */
  SHOW_CAPTION: { id: string; text: string; final: boolean };
  CLOSE_CAPTION: { id?: string };
  SHOW_QUESTION: {
    requestId: string;
    text: string;
    speak?: boolean;
    questionType?: RemoteQuestionType;
    /** 선택형 보기(2~6개) */
    choices?: string[];
    mode?: RemoteQuestionMode;
    answerKey?: RemoteAnswerKey;
    /** 선택형: 여러 개 골라 제출(정답이 2개 이상일 때) — 누락 시 하나만 고름 */
    multiSelect?: boolean;
    /** 정답모드: 오답일 때 정답 공개 여부 — 누락 시 true */
    revealAnswer?: boolean;
  };
  /** 현재 표시 중인 공지/질문/자막을 프레젠 기기에서 읽기. 자막은 완료된 문구(text)를 함께 보냄 */
  SPEAK_CURRENT: { targetKind: 'notice' | 'question' | 'caption'; text?: string };
  SUBMIT_ANSWER: {
    requestId: string;
    sessionId: string;
    deviceId: string;
    /** 단답 텍스트 · 'O'/'X' · 선택형은 '③ 보기' */
    content: string;
    /** 선택형: 0부터 시작하는 보기 번호 */
    choiceIndex?: number;
    /** 선택형 여러 개 선택 */
    choiceIndexes?: number[];
    answeredAt: number;
    /** 학생 기기 응답 중계(수업 프레젠 → 리모컨): 학생 식별자·표시 이름(번호+별칭) */
    studentId?: string;
    studentLabel?: string;
  };
  CLOSE_QUESTION: { requestId: string };
  /** 프레젠 기기 음성 목록 요청 → TTS_VOICES 로 요청자에게만 응답 */
  GET_TTS_VOICES: { includeAll: boolean };
  TTS_VOICES: { voices: RemoteTtsVoiceInfo[]; total: number; koCount: number; includeAll: boolean; settings: RemoteTtsSettings };
  SET_TTS_SETTINGS: { settings: RemoteTtsSettings };
  TTS_PREVIEW: { text?: string };
  /** pick 없으면: 일시정지 중이면 재개, 아니면 마지막 곡. random은 프레젠에서 모달과 같은 방식으로 선택 */
  /** pick: 리모컨에서 고른 곡(매번 전송). restart: 목록에서 새로 고름 → 같은 곡이어도 처음부터/새 랜덤 */
  MUSIC_PLAY: { pick?: RemoteBgmPick; restart?: boolean };
  MUSIC_PAUSE: Record<string, never>;
  MUSIC_STOP: Record<string, never>;
  COMMAND_ACK: { commandId: string; ok: boolean; error?: RemoteAckError; detail?: string };
  PRESENTATION_STATUS: Omit<RemotePresenceState, 'role'>;
  /** 프레젠 이름 변경 (이름은 프레젠 기기 LS에 저장되므로 명령으로 전달) */
  RENAME_PRESENTATION: { name: string };
  /** 리모컨 → 수업 프레젠: 슬라이드 이동 (ACK 없음 — 결과는 SLIDE_STATE). sync = 현재 상태만 요청 */
  SLIDE_NAV: { dir: RemoteSlideNavDir; index?: number };
  /** 수업 프레젠 → 리모컨: 현재 슬라이드 (index 0부터, total = 전체 수). 변경 시 전체에, sync 요청 시 요청자에게 */
  SLIDE_STATE: { index: number; total: number; label?: string };
  /** 리모컨 → 전체: 지금 열려 있는 프레젠 확인 (목록 열 때/연결 직후 1회 — 주기 폴링 아님) */
  PRESENCE_PING: { at: number };
  /** 프레젠 → 요청한 리모컨: 살아 있음 응답 */
  PRESENCE_PONG: { at: number };
}

export type RemoteSlideNavDir = 'prev' | 'next' | 'first' | 'last' | 'goto' | 'sync';

export interface RemoteTtsSettings {
  /** null = 기본 한국어 음성 */
  voiceURI: string | null;
  rate: number;
  pitch: number;
  volume: number;
}
export interface RemoteTtsVoiceInfo {
  voiceURI: string;
  name: string;
  lang: string;
  localService: boolean;
  default: boolean;
}

export type RemoteBgmPick = { moodId: string; mode: 'random' } | { moodId: string; mode: 'track'; track: number };

export type RemoteEventType = keyof RemotePayloadMap;

export type RemoteAckError =
  | 'autoplay_blocked'
  | 'tts_unavailable'
  | 'tts_blocked'
  | 'nothing_to_speak'
  | 'not_implemented'
  | 'invalid_payload'
  | 'unknown';

export interface RemoteEnvelope<T extends RemoteEventType = RemoteEventType> {
  type: T;
  /** 중복 제거·ACK 매칭용 */
  commandId: string;
  fromSessionId: string;
  /** 없으면 채널 전체 대상 */
  targetSessionId?: string;
  sentAt: number;
  payload: RemotePayloadMap[T];
}

export type AnyRemoteEnvelope = { [K in RemoteEventType]: RemoteEnvelope<K> }[RemoteEventType];

/** 확인 응답이 필요한 명령 */
export const REMOTE_ACK_REQUIRED: ReadonlySet<RemoteEventType> = new Set<RemoteEventType>([
  'SHOW_NOTICE',
  'CLOSE_NOTICE',
  'SHOW_QUESTION',
  'CLOSE_QUESTION',
  'SPEAK_CURRENT',
  'GET_TTS_VOICES',
  'SET_TTS_SETTINGS',
  'TTS_PREVIEW',
  'MUSIC_PLAY',
  'MUSIC_PAUSE',
  'MUSIC_STOP',
  'RENAME_PRESENTATION',
]);

/** (사용 안 함) Supabase presence는 주기 갱신하지 않음 — 참가/재연결/상태 변경 시에만 track */
export const REMOTE_HEARTBEAT_MS = 0;
/** 이 시간 이상 heartbeat가 없으면 리모컨 목록에서 숨김 */
export const REMOTE_STALE_MS = 150000;

export const REMOTE_BROADCAST_EVENT = 'remote';
export const REMOTE_TOPIC_PREFIX = 'cp-remote:';
export const remoteTopicForUser = (userId: string) => `${REMOTE_TOPIC_PREFIX}${userId}`;
